import { ActivityIcon } from '@jouzen/ecom-components/icons/ActivityIcon';
import { LinkIcon } from '@jouzen/ecom-components/icons/LinkIcon';
import { MoonIcon } from '@jouzen/ecom-components/icons/MoonIcon';
import { ReadinessIcon } from '@jouzen/ecom-components/icons/ReadinessIcon';
import { TemperatureIcon } from '@jouzen/ecom-components/icons/TemperatureIcon';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import Image from '@/app/components/Image';
import Feature from '@/app/components/LegacyLandingPage/components/Feature';
import Section from '@/app/components/LegacyLandingPage/components/Section';

interface IconLabelProps {
  readonly children: ReactNode;
  readonly textColorHex?: string;
  readonly iconLabel: string;
}

const IconLabel = ({
  children,
  textColorHex = '#A2D3E8',
  iconLabel,
}: IconLabelProps) => {
  const t = useTranslations();
  return (
    <div
      className="col-start-2 col-end-6 mb-10 flex items-center md:col-start-3 md:col-end-13"
      style={{ color: textColorHex }}
      data-cy="lp-header-icon"
    >
      <div
        className="mr-4 flex size-14 items-center justify-center rounded-xl border-2 border-solid"
        style={{ color: textColorHex, borderColor: textColorHex }}
      >
        {children}
      </div>
      {iconLabel && (
        <Typography variant="h6" Element="h3" weight="normal" color="inherit">
          {t(iconLabel)}
        </Typography>
      )}
    </div>
  );
};

const WaysToIntegrate = () => {
  const t = useTranslations();
  return (
    <Section padTop className="bg-slate-700 text-white">
      <LayoutGrid>
        <Typography
          Element="h3"
          variant="h4"
          color="inherit"
          className="col-main mb-8 text-left md:text-center lg:mb-32"
        >
          {t('ways_to_integrate_title')}
        </Typography>
      </LayoutGrid>

      <Feature
        body={t('developer_ways_integrate_copy_sleep')}
        src="developer_ways_integrate_src_sleep"
        alt="developer_ways_integrate_alt_sleep"
        icon={
          <IconLabel
            iconLabel="developer_ways_integrate_title_sleep"
            textColorHex="#A2D3E8"
          >
            <MoonIcon width={24} height={24} />
          </IconLabel>
        }
      />
      <Feature
        body={t('developer_ways_integrate_copy_readiness')}
        src="developer_ways_integrate_src_readiness"
        alt="developer_ways_integrate_alt_readiness"
        icon={
          <IconLabel
            iconLabel="developer_ways_integrate_title_readiness"
            textColorHex="#A2D3E8"
          >
            <ReadinessIcon width={24} height={24} />
          </IconLabel>
        }
        reverse
      />
      <Feature
        body={t('developer_ways_integrate_copy_activity')}
        src="developer_ways_integrate_src_activity"
        alt="developer_ways_integrate_alt_activity"
        icon={
          <IconLabel
            iconLabel="developer_ways_integrate_title_activity"
            textColorHex="#A2D3E8"
          >
            <ActivityIcon width={24} height={24} />
          </IconLabel>
        }
      />
      <Feature
        body={t('developer_ways_integrate_copy_reproductive_health')}
        src="developer_ways_integrate_src_reproductive_health"
        alt="developer_ways_integrate_alt_reproductive_health"
        icon={
          <IconLabel
            iconLabel="developer_ways_integrate_title_reproductive_health"
            textColorHex="#A2D3E8"
          >
            <TemperatureIcon width={24} height={24} />
          </IconLabel>
        }
        reverse
      />

      <div className="py-8">
        <LayoutGrid rowGap={32} className="items-center">
          <div className="col-main flex justify-center md:col-start-3 md:col-end-13 md:justify-normal xl:col-start-4">
            <Image
              alt="Oura - Apple Health - Google Health - Logos"
              src="developer/oura-developer-logos"
              width={216}
              height={243}
              format="png"
            />
          </div>
          <div className="col-main text-left md:col-start-15 md:col-end-22">
            <IconLabel
              iconLabel="developer_ways_integrate_title_platforms"
              textColorHex="#A2D3E8"
            >
              <LinkIcon width={24} height={24} />
            </IconLabel>
            <Typography data-cy="lp-feature-body" color="inherit">
              {t('developer_ways_integrate_copy_platforms')}
            </Typography>
          </div>
        </LayoutGrid>
      </div>
    </Section>
  );
};

export default WaysToIntegrate;
